import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  CornerDownLeft,
  Database,
  FolderClosed,
  Globe,
  LayoutDashboard,
  ListChecks,
  Moon,
  Plus,
  Search,
  Settings,
  Sun,
  Users,
} from 'lucide-react';
import { createPortal } from 'react-dom';
import { api } from '@/lib/api';
import { useThemeStore } from '@/store/theme';
import { formatDomain } from '@/lib/format';
import { cn } from '@/lib/cn';

/**
 * Palet perintah (Ctrl/Cmd+K).
 *
 * Tidak ada di rancangan awal, tapi begitu jumlah job bertambah, ini menjadi
 * cara tercepat untuk berpindah - pengguna berat hampir tidak menyentuh
 * sidebar lagi setelah menemukannya.
 */
export const CommandPalette = ({ open, onClose }) => {
  const navigate = useNavigate();
  const { isDark, toggle } = useThemeStore();
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  // Job hanya dicari ke server saat pengguna benar-benar mengetik.
  const { data: jobResults } = useQuery({
    queryKey: ['command-search', query],
    queryFn: () => api.get('/jobs', { params: { search: query, limit: 5 } }),
    enabled: open && query.trim().length >= 2,
    staleTime: 30_000,
    retry: false,
  });

  const commands = useMemo(() => {
    const navigation = [
      { id: 'nav-dashboard', label: 'Dashboard', icon: LayoutDashboard, group: 'Navigasi', run: () => navigate('/dashboard') },
      { id: 'nav-new', label: 'Buat Scraping baru', icon: Plus, group: 'Navigasi', run: () => navigate('/jobs/new') },
      { id: 'nav-jobs', label: 'List Job', icon: ListChecks, group: 'Navigasi', run: () => navigate('/jobs') },
      { id: 'nav-datasets', label: 'Data Publik', icon: Globe, group: 'Navigasi', run: () => navigate('/datasets') },
      { id: 'nav-storage', label: 'Penyimpanan Pribadi', icon: FolderClosed, group: 'Navigasi', run: () => navigate('/storage') },
      { id: 'nav-settings', label: 'Pengaturan', icon: Settings, group: 'Navigasi', run: () => navigate('/settings') },
    ];

    const actions = [
      {
        id: 'action-theme',
        label: isDark ? 'Ganti ke mode terang' : 'Ganti ke mode gelap',
        icon: isDark ? Sun : Moon,
        group: 'Tindakan',
        run: toggle,
      },
    ];

    const jobs = (jobResults?.data ?? []).map((job) => ({
      id: `job-${job.id}`,
      label: job.name,
      hint: formatDomain(job.targetUrl),
      icon: Database,
      group: 'Job',
      run: () => navigate(`/jobs/${job.id}`),
    }));

    const needle = query.trim().toLowerCase();
    const matches = (item) => !needle || item.label.toLowerCase().includes(needle);

    return [...jobs, ...navigation.filter(matches), ...actions.filter(matches)];
  }, [query, jobResults, navigate, isDark, toggle]);

  // Pilihan kembali ke atas setiap kali daftar berubah, supaya Enter tidak
  // menjalankan perintah yang tidak lagi terlihat.
  useEffect(() => setActiveIndex(0), [query, jobResults]);

  useEffect(() => {
    if (open) {
      setQuery('');
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  useEffect(() => {
    listRef.current
      ?.querySelector(`[data-index="${activeIndex}"]`)
      ?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  if (!open) return null;

  const execute = (command) => {
    command?.run();
    onClose();
  };

  const onKeyDown = (event) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((i) => (i + 1) % Math.max(1, commands.length));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((i) => (i - 1 + commands.length) % Math.max(1, commands.length));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      execute(commands[activeIndex]);
    } else if (event.key === 'Escape') {
      onClose();
    }
  };

  let lastGroup = null;

  return createPortal(
    <div className="fixed inset-0 z-[60] flex items-start justify-center px-4 pt-[12vh]">
      <div className="fixed inset-0 bg-brand-950/45 backdrop-blur-[2px]" onClick={onClose} aria-hidden="true" />

      <div
        role="dialog"
        aria-modal="true"
        aria-label="Palet perintah"
        className="relative w-full max-w-xl animate-in overflow-hidden rounded-2xl border border-[var(--border-subtle)] bg-[var(--surface-overlay)] shadow-xl"
        onKeyDown={onKeyDown}
      >
        <div className="flex items-center gap-3 border-b border-[var(--border-subtle)] px-4">
          <Search className="size-4 shrink-0 text-[var(--text-muted)]" aria-hidden="true" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Cari job, halaman, atau tindakan…"
            aria-label="Cari"
            className="h-12 w-full bg-transparent text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none"
          />
          <kbd className="shrink-0 rounded border border-[var(--border-default)] px-1.5 py-0.5 text-[10px] font-semibold text-[var(--text-muted)]">
            Esc
          </kbd>
        </div>

        <div ref={listRef} className="max-h-80 overflow-y-auto p-2">
          {commands.length === 0 ? (
            <p className="px-3 py-8 text-center text-sm text-[var(--text-muted)]">
              Tidak ada hasil untuk “{query}”.
            </p>
          ) : (
            commands.map((command, index) => {
              const showGroup = command.group !== lastGroup;
              lastGroup = command.group;
              const Icon = command.icon;

              return (
                <div key={command.id}>
                  {showGroup && (
                    <p className="px-3 pt-3 pb-1 text-[10px] font-bold tracking-wider text-[var(--text-muted)] uppercase">
                      {command.group}
                    </p>
                  )}
                  <button
                    type="button"
                    data-index={index}
                    onClick={() => execute(command)}
                    onMouseEnter={() => setActiveIndex(index)}
                    className={cn(
                      'flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm',
                      index === activeIndex
                        ? 'bg-[var(--surface-sunken)] text-[var(--text-primary)]'
                        : 'text-[var(--text-secondary)]',
                    )}
                  >
                    <Icon className="size-4 shrink-0 text-[var(--text-muted)]" aria-hidden="true" />
                    <span className="min-w-0 flex-1 truncate font-medium">{command.label}</span>
                    {command.hint && (
                      <span className="shrink-0 text-xs text-[var(--text-muted)]">
                        {command.hint}
                      </span>
                    )}
                    {index === activeIndex && (
                      <CornerDownLeft className="size-3.5 shrink-0 text-[var(--text-muted)]" />
                    )}
                  </button>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};
